import { useId } from "react";

import { cn } from "@/lib/utils";

interface Datum {
  label: string;
  /** Primary stacked-up value (drawn at the top). */
  value: number;
  /** Optional secondary value drawn underneath the primary. */
  secondaryValue?: number;
  /** Optional click handler — turns the bar into a button-like target. */
  onClick?: () => void;
}

interface BarChartProps {
  data: Datum[];
  height?: number;
  /** Format absolute numbers as e.g. "PHP 12,345.00" in the tooltip. */
  formatValue?: (value: number) => string;
  /** Label for the secondary value, used in the legend + tooltip. */
  secondaryLabel?: string;
  /** Label for the primary value, used in the legend + tooltip. */
  primaryLabel?: string;
  className?: string;
}

const DEFAULT_FORMAT = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 0 });

/** Minimal SVG bar chart with no third-party dependency. Renders one
 *  bar per datum, scaled to the largest absolute value across the
 *  primary + secondary series. Tooltip is a native <title>.
 *
 *  Use case: dashboard summaries where adding `recharts` would be
 *  overkill. Swap for a richer library when you need axes, stacked
 *  series, brushes, etc.
 */
export function BarChart({
  data,
  height = 160,
  formatValue = DEFAULT_FORMAT,
  primaryLabel = "Value",
  secondaryLabel,
  className,
}: BarChartProps) {
  const padding = { top: 8, right: 8, bottom: 24, left: 8 };
  const width = Math.max(data.length * 56, 280);
  const innerH = height - padding.top - padding.bottom;
  const innerW = width - padding.left - padding.right;
  const slot = data.length > 0 ? innerW / data.length : innerW;
  const barWidth = Math.max(Math.min(slot * 0.55, 36), 8);
  const id = useId();

  const max = Math.max(
    1,
    ...data.flatMap((d) => [d.value, d.secondaryValue ?? 0]),
  );

  function bar(d: Datum, i: number) {
    const cx = padding.left + slot * i + slot / 2;
    const primaryH = (d.value / max) * innerH;
    const secondaryH = ((d.secondaryValue ?? 0) / max) * innerH;
    const baseline = padding.top + innerH;

    const tip = secondaryLabel
      ? `${d.label}\n${primaryLabel}: ${formatValue(d.value)}\n${secondaryLabel}: ${formatValue(d.secondaryValue ?? 0)}`
      : `${d.label}\n${primaryLabel}: ${formatValue(d.value)}`;

    const bars = (
      <g aria-label={d.label}>
        <title>{tip}</title>
        {d.secondaryValue !== undefined && (
          <rect
            x={cx - barWidth / 2}
            y={baseline - secondaryH}
            width={barWidth}
            height={secondaryH}
            rx={2}
            className="fill-muted-foreground/40"
          />
        )}
        <rect
          x={cx - barWidth / 2}
          y={baseline - primaryH}
          width={barWidth}
          height={primaryH}
          rx={2}
          className="fill-primary"
        />
        <text
          x={cx}
          y={height - 8}
          textAnchor="middle"
          className="fill-muted-foreground text-[10px]"
        >
          {d.label}
        </text>
      </g>
    );

    return d.onClick ? (
      <g
        key={`${id}-${i}`}
        role="button"
        tabIndex={0}
        onClick={d.onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            d.onClick?.();
          }
        }}
        className="cursor-pointer hover:opacity-80 focus:outline-none focus-visible:opacity-80"
      >
        {bars}
      </g>
    ) : (
      <g key={`${id}-${i}`}>{bars}</g>
    );
  }

  return (
    <div className={cn("w-full overflow-x-auto", className)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        role="img"
        aria-label="Bar chart"
      >
        {/* baseline */}
        <line
          x1={padding.left}
          y1={padding.top + innerH}
          x2={width - padding.right}
          y2={padding.top + innerH}
          className="stroke-border"
          strokeWidth={1}
        />
        {data.map(bar)}
      </svg>
      {secondaryLabel && (
        <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
          <Legend swatchClass="bg-primary" label={primaryLabel} />
          <Legend
            swatchClass="bg-muted-foreground/40"
            label={secondaryLabel}
          />
        </div>
      )}
    </div>
  );
}

function Legend({ swatchClass, label }: { swatchClass: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("inline-block h-2 w-2 rounded-sm", swatchClass)} />
      {label}
    </span>
  );
}
